/**
 * useScriptActions Hook - MA-ARCH-163
 * 
 * Extracted from ScriptEdit.jsx - Handles save, add, delete, and update actions for nodes and options.
 */

import { useCallback } from 'react';
import { api } from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import { applyAutoProspectAssignment } from '../utils/scriptEditUtils';

/**
 * Hook for script node and option CRUD actions
 * @param {string} scriptId - The script ID
 * @param {Object} editorState - Editor state from useScriptEditorState
 * @param {boolean} canEdit - Whether user can edit
 */
export function useScriptActions(scriptId, editorState, canEdit) {
  const {
    active,
    nodeTitle,
    yourReply,
    scriptBlocks,
    coachingNote,
    nodeScope,
    scriptName,
    setActiveNodeId,
    fetchGraph,
    autoSaveDraftIfDirty,
    setIsRenaming,
    localNodes,
    graph,
    setIsNodeOrderDirty,
  } = editorState;

  // Save current node
  const onSaveNode = useCallback(async () => {
    if (!active) return;
    try {
      await api.put(`/nodes/${active.node.id}`, {
        title: nodeTitle || null,
        your_reply: yourReply,
        script_blocks: applyAutoProspectAssignment(scriptBlocks),
        coaching_note: coachingNote,
        scope: nodeScope,
      });
      toast({ title: "Node saved" });
      await fetchGraph();
    } catch (e) {
      toast({
        title: "Save failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  }, [active, nodeTitle, yourReply, scriptBlocks, coachingNote, nodeScope, fetchGraph]);

  // Add a new node
  const onAddNode = useCallback(async (title, reply) => {
    try {
      const res = await api.post(`/scripts/${scriptId}/nodes`, {
        title: title || null,
        your_reply: reply || "New node — write your line here.",
        coaching_note: "",
      });
      toast({ title: "Node created" });
      await fetchGraph();
      setActiveNodeId(res.data.id);
      return { success: true, id: res.data.id };
    } catch (e) {
      toast({
        title: "Create node failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
      return { error: e };
    }
  }, [scriptId, fetchGraph, setActiveNodeId]);

  // Delete current node
  const onDeleteNode = useCallback(async () => {
    if (!active) return;
    if (!window.confirm("Delete this node? Options pointing to it will be removed too.")) return;
    try {
      await api.delete(`/nodes/${active.node.id}`);
      toast({ title: "Node deleted" });
      setActiveNodeId(null);
      await fetchGraph();
    } catch (e) {
      toast({
        title: "Delete failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  }, [active, fetchGraph, setActiveNodeId]);

  // Set node as start
  const onSetStart = useCallback(async () => {
    if (!active) return;
    try {
      await api.post(`/scripts/${scriptId}/start/${active.node.id}`);
      toast({ title: "Start node updated" });
      await fetchGraph();
    } catch (e) {
      toast({
        title: "Failed to set start",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  }, [active, scriptId, fetchGraph]);

  // Save script name
  const onSaveScriptName = useCallback(async () => {
    if (!graph?.script) return;
    const next = scriptName.trim();
    if (!next) return;

    setIsRenaming(true);
    try {
      await api.put(`/scripts/${scriptId}`, { name: next });
      toast({ title: "Script title updated" });
      await fetchGraph();
    } catch (e) {
      toast({
        title: "Rename failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRenaming(false);
    }
  }, [graph?.script, scriptName, scriptId, fetchGraph, setIsRenaming]);

  // Save node order
  const saveNodeOrder = useCallback(async () => {
    if (!canEdit) return;
    const ids = (localNodes || graph?.nodes || []).map((w) => w.node.id);
    if (!ids.length) return;

    try {
      await api.put(`/scripts/${scriptId}/nodes/reorder`, { node_ids: ids });
      toast({ title: "Node order saved" });
      setIsNodeOrderDirty(false);
      await fetchGraph();
    } catch (e) {
      toast({
        title: "Reorder failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  }, [canEdit, localNodes, graph?.nodes, scriptId, fetchGraph, setIsNodeOrderDirty]);

  // Update collect_prospect_info_at setting
  const onUpdateCollectProspectInfoAt = useCallback(async (value) => {
    if (!canEdit) return;
    try {
      await api.put(`/scripts/${scriptId}`, { collect_prospect_info_at: value });
      await fetchGraph();
      toast({ title: "Saved" });
    } catch (e) {
      toast({
        title: "Could not save",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  }, [canEdit, scriptId, fetchGraph]);

  // Add option to current node
  const onAddOption = useCallback(async (prospectSays, targetId) => {
    if (!active) return { error: "No active node" };
    try {
      await autoSaveDraftIfDirty();
      await api.post(`/nodes/${active.node.id}/options`, {
        prospect_says: prospectSays,
        target_node_id: targetId,
        sort_order: active.options.length,
      });
      toast({ title: "Option added" });
      await fetchGraph();
      return { success: true };
    } catch (e) {
      toast({
        title: "Add option failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
      return { error: e };
    }
  }, [active, autoSaveDraftIfDirty, fetchGraph]);

  // Update an option
  const onUpdateOption = useCallback(async (optionId, patch) => {
    try {
      await autoSaveDraftIfDirty();
      await api.put(`/options/${optionId}`, patch);
      await fetchGraph();
    } catch (e) {
      toast({
        title: "Option update failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  }, [autoSaveDraftIfDirty, fetchGraph]);

  // Delete an option
  const onDeleteOption = useCallback(async (optionId) => {
    try {
      await autoSaveDraftIfDirty();
      await api.delete(`/options/${optionId}`);
      toast({ title: "Option removed" });
      await fetchGraph();
    } catch (e) {
      toast({
        title: "Delete option failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  }, [autoSaveDraftIfDirty, fetchGraph]);

  return {
    onSaveNode,
    onAddNode,
    onDeleteNode,
    onSetStart,
    onSaveScriptName,
    saveNodeOrder,
    onUpdateCollectProspectInfoAt,
    onAddOption,
    onUpdateOption,
    onDeleteOption,
  };
}

export default useScriptActions;
